import SimCardIcon from '@mui/icons-material/SimCard';
import { Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { type FC } from 'react';

import { type RouterNetworkSimSummary } from '@okvc/schema';

import { formatHoursMinutes } from '../../../utils/formatHoursMinutes.js';
import { CARRIER_LABELS } from '../routerSimForm.js';

const SLOT_LABELS = { physical: 'Physical SIM', esim: 'eSIM' } as const;

interface RouterSimCardProps {
  sim: RouterNetworkSimSummary;
  activeSince: string | null;
  minutesOnSim: number;
  days: number;
  onUseSim?: () => void;
  isSwitching?: boolean;
}

function activityLine(sim: RouterNetworkSimSummary, activeSince: string | null): string {
  if (sim.isActive) {
    return activeSince ? `Active since ${DateTime.fromISO(activeSince).toRelative()}` : 'Active';
  }
  return sim.lastActiveAt ? `Last active ${DateTime.fromISO(sim.lastActiveAt).toRelative()}` : 'Never used yet';
}

export function simActionLabel(sim: Pick<RouterNetworkSimSummary, 'isActive' | 'isPrimary'>): string | null {
  if (sim.isActive && sim.isPrimary) {
    return null;
  }
  return sim.isActive ? 'Make primary' : 'Use this SIM';
}

const RouterSimCard: FC<RouterSimCardProps> = ({ sim, activeSince, minutesOnSim, days, onUseSim, isSwitching = false }) => {
  const actionLabel = simActionLabel(sim);

  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 240 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <SimCardIcon color={sim.isActive ? 'primary' : 'disabled'} />
        <Typography variant="h6">{CARRIER_LABELS[sim.carrier]}</Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Chip size="small" label={sim.isActive ? 'Active' : 'Standby'} color={sim.isActive ? 'success' : 'default'} />
        <Chip size="small" variant="outlined" label={sim.isPrimary ? 'Primary' : 'Backup'} />
        <Chip size="small" variant="outlined" label={SLOT_LABELS[sim.slot]} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {activityLine(sim, activeSince)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {`${formatHoursMinutes(Math.round(minutesOnSim))} on this SIM in the last ${days} days`}
      </Typography>
      {onUseSim && actionLabel ? (
        <Button size="small" variant="outlined" sx={{ mt: 1.5 }} disabled={isSwitching} onClick={onUseSim}>
          {actionLabel}
        </Button>
      ) : null}
    </Paper>
  );
};

export default RouterSimCard;
